import URI from 'urijs'
import Vue from 'vue'
import { config } from '@/plugins/env.config'
import { status, types, orderBy, sponsors, authorizationTypes, voidableClaimStatuses } from '@/config/authorization'
import { currencySymbols } from '@/config/settlements';
import { decamelizeKeys, camelizeKeys } from 'humps'
import axios from 'axios';

async function getAuthorizations (providerSlug, filters, attemptNumber = 1) {
  const getAuthorizationsUri = new URI(config.apiBaseUrl)
  getAuthorizationsUri.segment('arc')
  getAuthorizationsUri.segment('v1')
  getAuthorizationsUri.segment('providers')
  getAuthorizationsUri.segment(providerSlug)
  getAuthorizationsUri.segment('outpatient-care-claim')

  if(config.theme === 'KONEKSI' && filters.sponsorAndCodeSearchValue){
    getAuthorizationsUri.segment('search-by-sponsor-and-osigu-id')
  }

  if (filters.createdFrom) {
    getAuthorizationsUri.addSearch('date_from', filters.createdFrom)
  }

  if (filters.createdTo) {
    getAuthorizationsUri.addSearch('date_to', filters.createdTo)
  }

  if (filters.statuses) {
    getAuthorizationsUri.addSearch('claim_status', filters.statuses)
  }

  if (filters.codeSearchValue) {
    if(config.theme === 'OSIGU'){
      getAuthorizationsUri.addSearch('authorization_code', filters.codeSearchValue)
      getAuthorizationsUri.addSearch('claim_id', filters.codeSearchValue)
    }else{
      if (config.showOsiguClaimIds === 'true') {
        getAuthorizationsUri.addSearch('claim_id', filters.codeSearchValue)
      } else {
        getAuthorizationsUri.addSearch('sponsor_transaction_id', filters.codeSearchValue)
      }
    }
  }

  if(filters.sponsorAndCodeSearchValue){
    if(config.theme === 'KONEKSI'){
      getAuthorizationsUri.addSearch('search_param_id', filters.sponsorAndCodeSearchValue)
    }else{
      if (config.showOsiguClaimIds === 'true') {
        getAuthorizationsUri.addSearch('claim_id', filters.sponsorAndCodeSearchValue)
      } else {
        getAuthorizationsUri.addSearch('authorization_code', filters.sponsorAndCodeSearchValue)
      }    
    }
  }

  if (filters.orderBy) {
    getAuthorizationsUri.addSearch('sort', filters.orderBy)
  }

  if(filters.satisfiesDocumentsPolicy){
    getAuthorizationsUri.addSearch('satisfies_documents_policy', filters.satisfiesDocumentsPolicy)
  }

  getAuthorizationsUri.addSearch('page', filters.page)
  getAuthorizationsUri.addSearch('size', filters.size)

  try {
    return await Vue.prototype.$http.get(getAuthorizationsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizations(providerSlug, filters, attemptNumber + 1)
    }

    throw error
  }
}

async function sendBiometricValidation(providerSlug, payload, attemptNumber = 1) {
  const biometricValidationUri = new URI(config.apiBaseUrl)
  biometricValidationUri.segment('arc')
  biometricValidationUri.segment('v1')
  biometricValidationUri.segment('providers')
  biometricValidationUri.segment(providerSlug)
  biometricValidationUri.segment('biometric-reading')

  const body = decamelizeKeys(payload)

  try {
    if(config.biometricValidation === 'true'){
      return await Vue.prototype.$http.post(biometricValidationUri.toString(), body)
    }
    return ({data: {verificationStatus: "VERIFICATION_SUCCESS"}})
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendBiometricValidation(providerSlug, payload, attemptNumber + 1)
    }

    throw error
  }
}

async function startClaimAuthorization(providerSlug, payload, attemptNumber = 1) {
  const startClaimAuthorizationUri = new URI(config.apiBaseUrl)
  startClaimAuthorizationUri.segment('arc')
  startClaimAuthorizationUri.segment('v1')
  startClaimAuthorizationUri.segment('providers')
  startClaimAuthorizationUri.segment(providerSlug)
  startClaimAuthorizationUri.segment('outpatient-care-claim')

  const body = decamelizeKeys(payload)

  try {
    return await Vue.prototype.$http.post(startClaimAuthorizationUri.toString(), body)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return startClaimAuthorization(providerSlug, payload, attemptNumber + 1)
    }

    throw response.data
  }
}

async function getClaimProcessMonitor(payload, attemptNumber = 1) {
  const monitorId = new URI(payload)

  try {
    const response = await Vue.prototype.$http.get(monitorId)

    if (response.data.status === 'PENDING') {
      const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 2000))
      return getClaimProcessMonitor(payload)
    }

    return response
  } catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getClaimProcessMonitor(payload, attemptNumber + 1)
    }

    throw error
  }
}

async function completeClaimAuthorization(providerSlug, claimId, payload, attemptNumber = 1) {
  const completeClaimUri = new URI(config.apiBaseUrl)
  completeClaimUri.segment('arc')
  completeClaimUri.segment('v1')
  completeClaimUri.segment('providers')
  completeClaimUri.segment(providerSlug)
  completeClaimUri.segment('outpatient-care-claim')
  completeClaimUri.segment(claimId.toString())
  completeClaimUri.segment('complete')

  const body = decamelizeKeys(payload)

  try {
    return await Vue.prototype.$http.post(completeClaimUri.toString(), body)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return completeClaimAuthorization(providerSlug, claimId, payload, attemptNumber + 1)
    }

    throw error
  }
}

async function voidClaimAuthorization(authorizationId, attemptNumber = 1) {
  const voidClaimUri = new URI(config.apiBaseUrl)
  voidClaimUri.segment('arc')
  voidClaimUri.segment('v1')
  voidClaimUri.segment('authorizations')
  voidClaimUri.segment(authorizationId.toString())
  voidClaimUri.segment('void')


  try {
    return await Vue.prototype.$http.post(voidClaimUri.toString(), {})
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return voidClaimAuthorization(authorizationId, attemptNumber + 1)
    }

    throw error
  }
}

async function cancelClaim(providerSlug, claimId, retrieveResponse = false, attemptNumber = 1){
  const voidClaimUri = new URI(config.apiBaseUrl)
  voidClaimUri.segment('arc')
  voidClaimUri.segment('v1')
  voidClaimUri.segment('providers')
  voidClaimUri.segment(providerSlug)
  voidClaimUri.segment('claims')
  voidClaimUri.segment(claimId.toString())
  voidClaimUri.segment('void')

  try{
    const response = await Vue.prototype.$http.post(voidClaimUri.toString(), {})
    if(retrieveResponse){
      if(response.status === 202){
        const location = response.headers.location
        if(location){
          return await pollMonitor(location)
        }
      }
    }
    return response
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return cancelClaim(providerSlug, claimId, retrieveResponse, attemptNumber + 1)
    }

    throw error
  }
}

function getStatus(authorization) {
  if(Object.prototype.hasOwnProperty.call(authorization, "coverageType")){
    const statusByCoverage = status.find(configuration => configuration.coverageType === authorization.coverageType)
    if (statusByCoverage) {
      const configurationState = statusByCoverage.states.find(state => state.value === authorization.authorizationStatus);
      return configurationState && configurationState.text || '';
    }
  }
  return getStatus({ coverageType: 'DEFAULT', authorizationStatus: authorization.authorizationStatus })
}

function getStatusWithoutCoverage(statusText){
  return getStatus({ coverageType: 'DEFAULT', authorizationStatus: statusText })
}

function getType(authorization) {
  const configurationType = types.find(type => {
    const productType = authorization.productType
    let transactionType = authorization.transactionType

    if(transactionType === 'DIAGNOSTIC_TESTS' && productType === 'LABORATORY'){
        transactionType = 'DIAGNOSTIC_TESTS'
    }

    if(transactionType === 'DIAGNOSTIC_TESTS' && productType === 'DIAGNOSTIC_TEST'){
      transactionType = 'DIAGNOSTIC_STUDY'
    }

    const transactionTypeValidator = type.slug === transactionType
    
    if(transactionTypeValidator){
      return type
    }
  });

  return configurationType && configurationType.text || '';
}

async function getAuthorizationDetail(providerSlug, authorizationId, attemptNumber = 1){
  const authorizationDetailURI = new URI(config.apiBaseUrl)
  authorizationDetailURI.segment('arc')
  authorizationDetailURI.segment('v2')
  authorizationDetailURI.segment('providers')
  authorizationDetailURI.segment(providerSlug)
  authorizationDetailURI.segment('claims')
  authorizationDetailURI.segment(authorizationId)

  try{
    return await Vue.prototype.$http.get(authorizationDetailURI.toString());
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizationDetail(providerSlug, authorizationId, attemptNumber + 1)
    }

    throw error
  }
}

async function getAuthorizationFile(providerSlug, authorizationFileId, attemptNumber = 1){
  const authorizationFileURI = new URI(config.apiBaseUrl)
  authorizationFileURI.segment('arc')
  authorizationFileURI.segment('v2')
  authorizationFileURI.segment('providers')
  authorizationFileURI.segment(providerSlug)
  authorizationFileURI.segment('outpatient-care-claim')
  authorizationFileURI.segment('files')
  authorizationFileURI.segment('support-claim-document')
  authorizationFileURI.segment(authorizationFileId)

  try{
    return await Vue.prototype.$http.get(authorizationFileURI.toString(), {responseType: 'arraybuffer'});
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizationFile(providerSlug, authorizationFileId, attemptNumber + 1)
    }

    throw error
  }
}


function generateAuthorizationDiagnosesPayload(payload) {
  const diagnoses = [];
  if(payload){
    payload.map((diagnosis) => {
      diagnoses.push({
        codingSystem: diagnosis.codingSystem,
        code: diagnosis.code,
        type: diagnosis.type
      })
    })
  }
  return diagnoses;
}

function generateAuthorizationTreatmentPayload(payload){

  const treatments = [];
  let treatmentRequest = {};

  if(payload){
    payload.map((treatment) => {

      treatmentRequest = {
        brand: treatment.brand,
        drugFormSlug: treatment.drugFormSlug,
        dosageMeasurementUnitSlug: treatment.dosageMeasurementUnitSlug,
        treatmentDurationDays: treatment.treatmentDurationDays,
        productIds: treatment.productIds,
        productId: treatment.productId,
        quantity: treatment.quantity ? treatment.quantity : 0,
        additionalNotes: treatment.additionalNotes,
        treatmentIndications: treatment.treatmentIndications,
        composition: treatment.composition ? treatment.composition : null
      }

      if(treatment.frequencies.length > 0){
        treatmentRequest["frequencies"] = treatment.frequencies
      }

      if(treatment.compositions){
        treatmentRequest["compositions"] = treatment.compositions
      }

      treatments.push(treatmentRequest)
    })
  }
  return treatments;
}

function generateAuthorizationProductsPayload(payload){
  const products = [];
  let productRequestModel = {};

  if(payload){
    payload.map((product) => {
      productRequestModel = {
        productId: product.id,
        productTypeSlug: product.type,
        quantity: product.quantity
      }

      if(product.providerProductCode){
        productRequestModel["providerProductCode"] = product.providerProductCode
      }

      products.push(productRequestModel);
    })
  }

  return products;
}

function createAuthorizationRequestPayload(authorizationStateData, userInformationState){

  const doctorFieldsValues = authorizationStateData.authorizationFormValues.doctorFields
  const doctorFieldsFinderValues = authorizationStateData.authorizationFormValues.doctorFields.doctorInformation
  const beneficiaryId = authorizationStateData.beneficiarySelected.id
  const beneficiaryPhoneNumber = authorizationStateData.beneficiary.phoneNumber
  const sponsorSlug = authorizationStateData.newAuthorization.beneficiarySearch.sponsorSlug
  const doctorCountryCode = userInformationState.tokenExtensions.countryCode
  const biometricReadingId = authorizationStateData.biometricVerificationResult
  
  const products = authorizationStateData.authorizationFormValues.doctorFields.products;
  const hasMedicalProcedure = products.some(product => product.type === 'medical-procedure');

  let riskManager = false;
  if (hasMedicalProcedure) {
    riskManager = authorizationStateData.authorizationFormValues.doctorFields.riskManager ?? false;
  }

  const diagnosesFormData = doctorFieldsValues.diagnoses;
  const treatmentsFormData = doctorFieldsValues.treatments;
  const productsFormData = doctorFieldsValues.products;

  const diagnosesPayload = generateAuthorizationDiagnosesPayload(diagnosesFormData);
  const treatmentsPayload = generateAuthorizationTreatmentPayload(treatmentsFormData);
  const productsPayload = generateAuthorizationProductsPayload(productsFormData);

  let authorizationRequestPayload = decamelizeKeys({
      beneficiaryId: beneficiaryId,
      beneficiaryEmail: null,
      beneficiaryPhoneNumber: beneficiaryPhoneNumber,
      sponsorSlug: sponsorSlug,
      doctorMedicalLicenseNumber: doctorFieldsValues.license,
      doctorSpecialtyName: doctorFieldsValues.specialty,
      doctorName: doctorFieldsValues.name || doctorFieldsFinderValues.name,
      illnessOnset: doctorFieldsValues.illnessOnset,
      doctorCountryCode: doctorCountryCode,
      riskManager: riskManager
  });

  const authorizationFormHasDiagnoses = diagnosesPayload ? diagnosesPayload.length > 0 : false
  const authorizationFormHasTreatments = treatmentsPayload ? treatmentsPayload.length > 0 : false
  const authorizationFormHasProducts = productsPayload ? productsPayload.length > 0 : false
  const authorizationFormHasBiometricId = biometricReadingId.id ? true : false

  if(authorizationFormHasDiagnoses){
    authorizationRequestPayload["diagnoses"] = decamelizeKeys(diagnosesPayload)
  }

  if(authorizationFormHasTreatments){
    authorizationRequestPayload["treatments"] = decamelizeKeys(treatmentsPayload)
  }

  if(authorizationFormHasProducts){
    authorizationRequestPayload["products"] = decamelizeKeys(productsPayload)
  }

  if(authorizationFormHasBiometricId){
    authorizationRequestPayload["biometricReadingId"] = biometricReadingId.id
  }

  return authorizationRequestPayload
}

async function createAuthorizationRequest(authorizationRequestPayload, transactionType, authorizationFiles, attemptNumber = 1) {
  const createAuthorizationRequestURI = new URI(config.apiBaseUrl)
  createAuthorizationRequestURI.segment('arc')
  createAuthorizationRequestURI.segment('v1')
  createAuthorizationRequestURI.segment('authorization-requests')

  try {
    const response = await Vue.prototype.$http.post(createAuthorizationRequestURI.toString(), authorizationRequestPayload)
    if(response.status === 202){
      const location = response.headers.location

      if (location) {
        const monitor = await getAuthorizationMonitor(location);
        const monitorData = camelizeKeys(monitor.data);
        const authorizationRequestId = monitorData.id;
        const authorizations = monitorData.authorizations;
       
        await executeUploadAuthorizationFiles(authorizations, transactionType, authorizationFiles, authorizationRequestId)
        await completeAuthorizationRequest(monitorData.id);
        return monitorData
      }
    }

  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return createAuthorizationRequest(authorizationRequestPayload, transactionType, authorizationFiles, attemptNumber + 1)
    }

    throw error
  }
}

async function executeUploadAuthorizationFiles( authorizations, transactionType, authorizationFiles, authorizationRequestId){
  if(!authorizationFiles){
    return
  }

  if(authorizationFiles.length === 0){
    return
  }

  for(const authorization of authorizations){
    const authorizationId = authorization.id;
    const documentCoverageSlug = transactionType;
    const filePromises = authorizationFiles.map(authorizaitonFile => uploadAuthorizationFile(authorizaitonFile.file,
        authorizationRequestId,
        authorizationId,
        documentCoverageSlug,
        authorizaitonFile.slug))
    const fileProcessResult = await Promise.allSettled(filePromises);

    fileProcessResult
      .filter(promiseResult => promiseResult.status === 'rejected')
      .forEach(error => {throw error.reason});
  }
}

async function uploadAuthorizationFile(authorizationFile, authorizationRequestId, authorizationId, documentCoverageSlug, documentTypeSlug, providerProductCode = null, productType = null, attemptNumber = 1){
  const authorizationFileURI = new URI(config.apiBaseUrl)
  authorizationFileURI.segment('arc')
  authorizationFileURI.segment('v1')
  authorizationFileURI.segment('authorization-requests')
  authorizationFileURI.segment(authorizationRequestId.toString())
  authorizationFileURI.segment('files')

  const requestConfig = {
    params: {
      authorization_id: authorizationId,
      coverage: documentCoverageSlug,
      document_type_slug: documentTypeSlug,
      provider_product_code: providerProductCode,
      product_type: productType
    }
  }

  const formData = new FormData();
  formData.append('file', authorizationFile)

  try{
    return await Vue.prototype.$http.post(authorizationFileURI.toString(), formData, requestConfig)
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return uploadAuthorizationFile(authorizationFile, authorizationRequestId, authorizationId, documentCoverageSlug, attemptNumber + 1)
    }
    throw error
  }
}

async function uploadAuthorizationRequestFile(authorizationFile, authorizationRequestId, authorizationId, documentCoverageSlug, documentTypeSlug, providerProductCode = null, productType = null, authorizationFileId = null, attemptNumber = 1){
  const authorizationFileURI = new URI(config.apiBaseUrl)
  authorizationFileURI.segment('arc')
  authorizationFileURI.segment('v1')
  authorizationFileURI.segment('authorization-requests')
  authorizationFileURI.segment(authorizationRequestId.toString())
  authorizationFileURI.segment('upload')
  authorizationFileURI.segment('files')

  const requestConfig = {
    params: {
      authorization_id: authorizationId,
      file_id: authorizationFileId,
      coverage: documentCoverageSlug,
      document_type_slug: documentTypeSlug,
      provider_product_code: providerProductCode,
      product_type: productType
    }
  }

  const formData = new FormData();
  formData.append('file', authorizationFile)

  try{
    return await Vue.prototype.$http.post(authorizationFileURI.toString(), formData, requestConfig)
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return uploadAuthorizationRequestFile(authorizationFile, authorizationRequestId, authorizationId, documentCoverageSlug, documentTypeSlug, providerProductCode, productType, authorizationFileId, attemptNumber + 1)
    }
    throw error
  }
}

async function deleteAuthorizationFile(payload ,attemptNumber = 1){
  const {
    authorizationRequestId,
    fileId,
    authorizationId,
    documentCoverageSlug,
    documentTypeSlug,
    productType
  } = payload
  
  const authorizationFileURI = new URI(config.apiBaseUrl)
  authorizationFileURI.segment('arc')
  authorizationFileURI.segment('v1')
  authorizationFileURI.segment('authorization-requests')
  authorizationFileURI.segment(authorizationRequestId.toString())
  authorizationFileURI.segment('files')
  authorizationFileURI.segment('delete')
  
  authorizationFileURI.addSearch('file_id', fileId)
  authorizationFileURI.addSearch('authorization_id', authorizationId)
  authorizationFileURI.addSearch('coverage', documentCoverageSlug)
  authorizationFileURI.addSearch('document_type_slug', documentTypeSlug)
  authorizationFileURI.addSearch('product_type', productType)

  try{
    return await Vue.prototype.$http.post(authorizationFileURI.toString())
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return deleteAuthorizationFile( authorizationRequestId, fileId, authorizationId, documentCoverageSlug, attemptNumber + 1)
    }
    throw error
  }
}

async function completeAuthorizationRequest(authorizationRequestId, attemptNumber = 1) {
  const completePrescriptionURI = new URI(config.apiBaseUrl)
  completePrescriptionURI.segment('arc');
  completePrescriptionURI.segment('v1');
  completePrescriptionURI.segment('authorization-requests');
  completePrescriptionURI.segment(authorizationRequestId.toString());
  completePrescriptionURI.segment('complete');

  try {
    return await Vue.prototype.$http.post(completePrescriptionURI.toString())
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return completeAuthorizationRequest(authorizationRequestId, attemptNumber + 1)
    }

    throw error
  }
}

async function getAuthorizationMonitor(payload, attemptNumber = 1) {
  const monitorId = new URI(payload)
  const getTokenHeaders = {
    Authorization: `Bearer ${localStorage.getItem('clientToken')}`
  }

  try {
    const response = await axios.get(monitorId.toString(), {
      headers: getTokenHeaders
    })

    if (response.data.status === 'PENDING') {
      const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 2000))
      return getAuthorizationMonitor(payload)
    }

    if (response.data.status === 'ERROR') {
      throw response
    }

    return response
  } catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const status = error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizationMonitor(payload, attemptNumber + 1)
    }

    throw error
  }
}

async function getPrescriptionFiles(authorizationFileId, attemptNumber = 1){
  const authorizationFileURI = new URI(config.apiBaseUrl)
  authorizationFileURI.segment('arc')
  authorizationFileURI.segment('v2')
  authorizationFileURI.segment('authorizations')
  authorizationFileURI.segment('files')
  authorizationFileURI.segment(authorizationFileId)

  try{
    return await Vue.prototype.$http.get(authorizationFileURI.toString(), {responseType: 'arraybuffer'});
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizationFile(authorizationFileId, attemptNumber + 1)
    }

    throw error
  }
}


function getSponsorText(sponsorToFind) {
    let itemFound = sponsors.find(sponsor => {
        if(sponsor.value === sponsorToFind){
            return sponsor.text
        }
    })
    if(itemFound){
        return itemFound.text
    }
}

function sanitizeAmount(currencyCode, amount, notNull){
    let localeAmount = 0;

    if(amount){
        localeAmount = amount.toLocaleString(undefined, {minimumFractionDigits: 2})
    }
    else if(notNull){
        localeAmount = "0.00"
    }
    else{
        return ''
    }

    let currencyConfig = currencySymbols.find(symbol => symbol.code === currencyCode)
    return `${currencyCode ? currencyCode : ''} ${currencyConfig ? currencyConfig.symbol : ''}${localeAmount}`
}

function osgCapitalize(text){
  if(typeof text !== 'string') return text
  text = text.toLowerCase()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function getSponsorCategories(sponsorSlug) {
  let itemFound = sponsors.find(sponsor => {
    if(sponsor.value === sponsorSlug){
      if(sponsor.categories){
        return sponsor
      }
    }
  })

  if(itemFound){
    return itemFound.categories
  }
  return null
}

async function getProviderProductTypes(providerBranchId, attemptNumber = 1){
  const providerProductsURI  = new URI(config.apiBaseUrl)
  providerProductsURI.segment('arc')
  providerProductsURI.segment('v1')
  providerProductsURI.segment('providers')
  providerProductsURI.segment('providers-branches')
  providerProductsURI.segment(providerBranchId)
  providerProductsURI.segment('product-types')

  try{
    return await Vue.prototype.$http.get(providerProductsURI.toString())
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getProviderProductTypes(providerBranchId, attemptNumber + 1)
    }

    throw error
  }
}


function findSponsorFeature(featureToFind, sponsorSlug){

  const sponsorFound = sponsors.find(sponsor => {
    if(sponsor.value === sponsorSlug){
      return sponsor
    }
  })

  if(sponsorFound){
    if(sponsorFound.features){
      const featureFound = sponsorFound.features.find(feature => feature === featureToFind)
      if(featureFound){
        return true
      }
    }
  }

  return false;
}

async function getSponsorDocumentPolicy(providerSlug, sponsorSlug, productType, attemptNumber = 1){
  const sponsorDocumentPolicyURI = new URI(config.apiBaseUrl)
  sponsorDocumentPolicyURI.segment('arc')
  sponsorDocumentPolicyURI.segment('v1')
  sponsorDocumentPolicyURI.segment('providers')
  sponsorDocumentPolicyURI.segment(providerSlug)
  sponsorDocumentPolicyURI.segment('sponsors')
  sponsorDocumentPolicyURI.segment(sponsorSlug)
  sponsorDocumentPolicyURI.segment('document-policy')
  sponsorDocumentPolicyURI.segment('authorizations')

  sponsorDocumentPolicyURI.addSearch('product_types', productType)

  try{
    return await Vue.prototype.$http.get(sponsorDocumentPolicyURI.toString())
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getSponsorDocumentPolicy(providerSlug, sponsorSlug, productType, attemptNumber + 1)
    }

    throw error
  }
}

async function getSponsorConfiguration(sponsorSlug, attemptNumber = 1){
  const sponsorConfigurationURI = new URI(config.apiBaseUrl)
  sponsorConfigurationURI.segment('arc')
  sponsorConfigurationURI.segment('v1')
  sponsorConfigurationURI.segment('sponsors')
  sponsorConfigurationURI.segment(sponsorSlug)
  sponsorConfigurationURI.segment('configuration')

  try{
    return await Vue.prototype.$http.get(sponsorConfigurationURI.toString())
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getSponsorConfiguration(sponsorSlug, attemptNumber + 1)
    }

    throw error
  }
}

function providerClaimAuthorizationIsVoidable(claim){
  if(claim){
    const status = claim.claimStatus || claim.status

    if(claim.isRequiredExternalSponsorValidations && config.theme === "KONEKSI"){
      const statusValidity = voidableClaimStatuses.includes(status);
      const externalValidity = claim.sponsorTransactionId ? true : false
      return statusValidity && externalValidity
    }

    return voidableClaimStatuses.includes(status)
  }

  return false
}

async function getAuthorizationDocumentPolicy(authorizationId, attemptNumber = 1) {
  const getAuthorizationDocumentPolicyUri = new URI(config.apiBaseUrl)
  getAuthorizationDocumentPolicyUri.segment('arc')
  getAuthorizationDocumentPolicyUri.segment('v2')
  getAuthorizationDocumentPolicyUri.segment('authorizations')
  getAuthorizationDocumentPolicyUri.segment(authorizationId.toString())
  getAuthorizationDocumentPolicyUri.segment('document-policy')

  try {
    return await Vue.prototype.$http.get(getAuthorizationDocumentPolicyUri.toString())
  } catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizationDocumentPolicy(authorizationId, attemptNumber + 1)
    }

    throw error
  }
}

async function pollMonitor(location, attemptNumber = 1){
  const pollMonitorURI = new URI(location)
  try{
    const response = await Vue.prototype.$http.get(pollMonitorURI.toString())

    if(response.data.status === 'IN_PROGRESS'){
      const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 2000))
      return pollMonitor(location)
    }

    if (response.data.status === 'PENDING') {
      const maxAttempts = config.getTokenMaxAttempts
      const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

      if (attemptNumber < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 2000))
        return pollMonitor(location, attemptNumber + 1)
      }

      throw 'error';
    }

    if (response.data.status === 'ERROR') {
      throw response
    }

    return response
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return pollMonitor(location, attemptNumber + 1)
    }

    throw error
  }
}

export {
  getSponsorConfiguration,
  getAuthorizations,
  sendBiometricValidation,
  startClaimAuthorization,
  getClaimProcessMonitor,
  completeClaimAuthorization,
  getStatus,
  getType,
  status,
  orderBy,
  getAuthorizationDetail,
  getSponsorText,
  sanitizeAmount,
  getStatusWithoutCoverage,
  getAuthorizationFile,
  generateAuthorizationDiagnosesPayload,
  createAuthorizationRequest,
  generateAuthorizationTreatmentPayload,
  uploadAuthorizationFile,
  deleteAuthorizationFile, 
  completeAuthorizationRequest,
  getAuthorizationMonitor,
  osgCapitalize,
  getPrescriptionFiles,
  voidClaimAuthorization,
  getSponsorCategories,
  getProviderProductTypes,
  authorizationTypes,
  generateAuthorizationProductsPayload,
  createAuthorizationRequestPayload,
  findSponsorFeature,
  cancelClaim,
  getSponsorDocumentPolicy,
  providerClaimAuthorizationIsVoidable,
  getAuthorizationDocumentPolicy,
  uploadAuthorizationRequestFile
}
